import { numToPriceFormat } from '../lib/money'
import { Cart, Session, CartCustomer, CartLine, TicketQuantities } from '../types/booking'

type TransformLinesToItemsArgs = {
  cart?: Cart | null
  quantities?: TicketQuantities
  session?: Session | null
  tickets?: unknown
}

type Ga4Item = {
  item_id: string
  item_name: string
  item_category: string
  item_variant: string
  quantity: number
  currency?: 'EUR'
  price?: string
}

type DataLayerEntry = {
  event: string
} & Record<string, unknown>

declare global {
  interface Window {
    dataLayer?: DataLayerEntry[]
  }
}

function getCartLines(cart: Cart | null | undefined): CartLine[] {
  if (!cart) return []
  if (Array.isArray(cart.lines) && cart.lines.length > 0) {
    return cart.lines
  }
  return []
}

function toQuantity(value: number | undefined): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 0
  return Math.max(0, value)
}

const totalTax = (cart: Cart): number => {
  return getCartLines(cart).reduce((sum, line) => sum + (line.vatValue ?? 0), 0)
}

export const transformLinesToItems = ({
  cart,
  quantities,
  session,
}: TransformLinesToItemsArgs): Ga4Item[] => {
  const items: Ga4Item[] = []
  const cartLines = getCartLines(cart)

  if (cartLines.length > 0) {
    cartLines.forEach((line) => {
      const ticketVariant = line.category
      //const variantId = line.physical?.name ?? ticketVariant ?? 'none'

      items.push({
        item_id: 'ticket_standard',
        item_name: 'Ticket Standard',
        item_category: ticketVariant,
        item_variant: ticketVariant,
        quantity: toQuantity(line.quantity),
        currency: 'EUR',
        price: numToPriceFormat(line.lineValue ?? 0),
      })
    })

    return items
  }

  if (quantities) {
    quantities.forEach((quantityItem) => {
      const item: Ga4Item = {
        item_id: 'ticket_standard',
        item_name: 'Ticket Standard',
        item_category: quantityItem.category,
        item_variant: quantityItem.category,
        quantity: toQuantity(quantityItem.quantity),
      }

      if (session) {
        item.currency = 'EUR'
        item.price = numToPriceFormat(
          session.pricesByCategory.find((i) => i.category === quantityItem.category)?.price ?? 0,
        )
      }

      items.push(item)
    })
  }

  return items
}

export const trackEvent = (event: string, params: Record<string, unknown> = {}) => {
  if (typeof window === 'undefined') return

  window.dataLayer = window.dataLayer ?? []
  window.dataLayer.push({
    event,
    ...params,
  })
}

export const moveStage = (stage: number) => {
  trackEvent('move_stage', { stage })
}

export const viewItemList = (items: Ga4Item[]) => {
  trackEvent('view_item_list', { items })
}

export const selectItem = (id: string, name: string) => {
  trackEvent('select_item', {
    items: [{ item_id: id, item_name: name }],
  })
}

const addToCart = (items: Ga4Item[]) => {
  trackEvent('add_to_cart', { items })
}

export const addCustomerData = (customer: CartCustomer) => {
  trackEvent('add_customer_data', {
    user_data: {
      email_address: customer.email ?? null,
      phone_number: customer.phone ?? null,
      address: {
        first_name: customer.firstName ?? null,
        last_name: customer.lastName ?? null,
        country: customer.country ?? null,
      },
    },
  })
}

export const selectSession = (session: Session) => {
  trackEvent('select_session', {
    uuid: session.id ?? session.id ?? null,
    start: session.dateTime ?? null,
    soldOut: session.vacancies === 0,
    availableSeats: session.vacancies ?? null,
  })
}

export const beginCheckout = (items: Ga4Item[]) => {
  trackEvent('begin_checkout', { items })
}

export const addDiscountCode = (code: string, isValid: boolean) => {
  trackEvent('add_discount_code', {
    discount_code: code,
    is_valid: isValid,
  })
}

export const selectPaymentMethod = (paymentMethod: string) => {
  trackEvent('select_payment_method', {
    payment_method: paymentMethod,
  })
}

export const addPaymentInfo = (
  paymentMethod: string,
  items: Ga4Item[],
  callback?: (() => void) | null,
) => {
  const payload: Record<string, unknown> = {
    payment_type: paymentMethod,
    items,
  }

  if (typeof callback === 'function') {
    payload.event_callback = callback
    payload.event_timeout = 2000
  }

  trackEvent('add_payment_info', payload)
}

export const googleAdsConversion = (cart: Cart) => {
  const transactionId = cart.id ?? cart.id ?? null
  const total = cart.total ?? 0
  const country = cart.customer?.country ?? null

  trackEvent('set_user_data', {
    user_data: {
      email: cart.customer.email ?? null,
      phone_number: cart.customer.phone ?? null,
      address: {
        first_name: cart.customer.firstName,
        last_name: cart.customer.lastName,
        country,
      },
    },
  })

  trackEvent('conversion', {
    send_to: 'AW-335319491/jP4-CMC855gaEMOj8p8B',
    transaction_id: transactionId,
    value: total,
    currency: 'EUR',
    user_country: country,
  })
}

export const purchase = (cart: Cart, items: Ga4Item[]) => {
  const transactionId = cart.id ?? cart.id ?? null
  const total = cart.total ?? 0
  const tax = totalTax(cart)
  const coupon = cart.discount ?? null
  const country = cart.customer.country ?? null

  trackEvent('purchase', {
    transaction_id: transactionId,
    value: total,
    tax,
    currency: 'EUR',
    coupon,
    items,
    user_data: {
      email_address: cart.customer.email ?? null,
      phone_number: cart.customer.phone ?? null,
      address: {
        first_name: cart.customer.firstName,
        last_name: cart.customer.lastName,
        country,
      },
    },
  })
}

const trackingGa4 = {
  transformLinesToItems,
  moveStage,
  viewItemList,
  selectItem,
  addToCart,
  addCustomerData,
  selectSession,
  beginCheckout,
  addDiscountCode,
  selectPaymentMethod,
  addPaymentInfo,
  googleAdsConversion,
  purchase,
}

export default trackingGa4
